import { DynamoFile, FileStatus } from '../types'
import { useQuery, useQueryClient } from 'react-query'
import {
  listAllFilesV2,
  listSuccessFiles,
  listSuccessFilesAndFolders,
} from '../api'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { pipe } from 'fp-ts/function'
import * as RA from 'fp-ts/ReadonlyArray'
import {
  QUERY_CACHE_TIME,
  QUERY_POLLING_INTERVAL,
  QUERY_STALE_TIME,
} from '../constant/query'

const containsProcessingFiles = (files: DynamoFile[]) =>
  files.find(
    (file) =>
      file.status === FileStatus.PROCESSING ||
      file.status === FileStatus.UPLOADING ||
      file.status === FileStatus.REDACTING
  )

const isProcessingFile = (file: DynamoFile) =>
  file.status === FileStatus.PROCESSING ||
  file.status === FileStatus.UPLOADING ||
  file.status === FileStatus.REDACTING

const useFiles = () => {
  const preSuccessFileRecords = useRef(new Set<string>())
  const client = useQueryClient()
  const { isError, refetch, isFetching, isLoading, error, isSuccess, data } =
    useQuery('ALL_FILES', listAllFilesV2, {
      refetchInterval: (data) => {
        const result = containsProcessingFiles(data || [])
        return result ? QUERY_POLLING_INTERVAL : false
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      select: (data) => {
        if (!data || !Array.isArray(data)) {
          return []
        }
        return data.sort((a, b) => dayjs(b.createdAt).diff(a.createdAt))
      },
      staleTime: QUERY_STALE_TIME, // 5 minutes
      cacheTime: QUERY_CACHE_TIME, // 10 minutes
      onSuccess: (data) => {
        if (!data || !Array.isArray(data)) {
          return
        }

        const result = pipe(data, RA.partition(isProcessingFile))

        const successFiles = result.left
        const processingFiles = result.right

        if (
          processingFiles.length &&
          (preSuccessFileRecords.current.size === 0 ||
            successFiles.some(
              (file) => !preSuccessFileRecords.current.has(file.fileId)
            ))
        ) {
          preSuccessFileRecords.current = new Set(
            successFiles.map((file) => file.fileId)
          )
          Promise.all([
            client.invalidateQueries('SUCCESS_FILES'),
            client.invalidateQueries('SUCCESS_FILES_AND_FOLDERS'),
          ]).catch((err) => {
            console.error('Failed to invalidate queries:', err)
          })
        } else if (processingFiles.length === 0) {
          Promise.all([
            client.invalidateQueries('SUCCESS_FILES'),
            client.invalidateQueries('SUCCESS_FILES_AND_FOLDERS'),
          ])
            .then(() =>
              Promise.all([
                client.prefetchQuery('SUCCESS_FILES', listSuccessFiles),
                client.prefetchQuery(
                  'SUCCESS_FILES_AND_FOLDERS',
                  listSuccessFilesAndFolders
                ),
              ])
            )
            .catch((err) => {
              console.error('Failed to prefetch queries:', err)
            })
        }
      },
    })
  return {
    files: data || [],
    isError,
    refetch,
    isFetching,
    isLoading,
    error,
    isSuccess,
  } as const
}

export default useFiles
